<template>
  <Facet :facet="facet">
    <div
      ref="checkboxRef"
      class="checkBoxesContainer"
      :class="[disableScroll, slideoutHeightFit, sizeShow, colorShow]"
    >
      <Checkbox
        v-if="criteriaOptions?.length && !renderDuringSsr"
        :model-value="criteriaCheckbox"
        :spacing="'margin-bottom-'+checkboxSpacing"
        name="criteria-checkbox"
        :options="criteriaOptions"
        :required="false"
        @update:modelValue="updateCheckboxCriteria($event); setPosition()"
      />
      <FacetCheckbox
        v-else-if="criteriaOptions?.length && renderDuringSsr"
        :model-value="criteriaCheckbox"
        :spacing="'margin-bottom-'+checkboxSpacing"
        name="criteria-checkbox"
        :options="criteriaOptions"
        :required="false"
        :show-checkbox="showCheckbox"
        @update:modelValue="updateCheckboxCriteria"
      />
      <div
        v-else
        class="medium facet-no-values-message"
        tabindex="0"
      >
        {{ noValuesFoundMessage }}
      </div>
    </div>
    <slot />
    <MultiSelectButtons
      :facet="facet"
      :parent-facet="parentFacet"
      :update-checkbox-criteria="updateCheckboxCriteria"
      :is-below-max-height="isBelowMaxHeight"
      @onClearAll="$emit('onClearAll')"
    />
  </Facet>
</template>

<script setup>
import {
  defineExpose, ref,
} from 'vue';
import Checkbox from '@atomic-ui/checkbox';
import Facet from './Facet';
import useMultiSelectDefault from '../../composables/useMultiSelectDefault';
import MultiSelectButtons from './MultiSelectButtons';
import FacetCheckbox from './FacetCheckbox';
import useRestoreScroll from '../../composables/useRestoreScroll';

const props = defineProps({
  facet: {
    type: Object,
    required: true,
  },
  updateSelected: {
    type: Function,
    default: null,
  },
  customValueRenderer: {
    type: Function,
    default: null,
  },
  noValuesFoundMessage: {
    type: String,
    default: "We couldn't find a match.",
  },
  onInit: {
    type: Function,
    default: null,
  },
  scrollToSelected: {
    type: Boolean,
  },
  isDynamic: {
    type: Boolean,
    default: false,
  },
  parentFacet: {
    type: Object,
    required: false,
  },
  renderDuringSsr: {
    type: Boolean,
    default: false,
  },
  showCheckbox: {
    type: Boolean,
    default: true,
  },
  shouldRestoreScroll: {
    type: Boolean,
  },
});

const checkboxRef = ref();

const {
  isBelowMaxHeight,
  criteriaCheckbox,
  criteriaOptions,
  updateCheckboxCriteria,
  checkboxSpacing,
  disableScroll,
  slideoutHeightFit,
  sizeShow,
  colorShow,
} = useMultiSelectDefault(props, checkboxRef);

const { setPosition } = useRestoreScroll(checkboxRef, props.facet.name, props.shouldRestoreScroll);

defineExpose({ updateCheckboxCriteria });
</script>

<style scoped lang="scss">
@import "src/scss/mixins";
.checkBoxesContainer {
  overflow-y: auto;
  max-height: rem-calc(304);

  &.slideout-height-fit {
    max-height:  fit-content;
    :deep(.checkbox-container) {
      .checkbox-wrapper .size-value-container {
        margin: 0 10px 24px 0;
      }
    }
  }

  @include customScrollbar();

  @include breakpoint (medium down) {
    max-height: rem-calc(224);
  }
}

.size-show.checkBoxesContainer {
  --size-facet-m-r: -5px;
  :deep(.checkbox-container) {
    display: flex;
    flex-wrap: wrap;
    margin-right: 0rem;
    .checkbox-wrapper .size-value-container {
       margin: 0 12px 24px 0;
       width: 72px;
    }
  }
}

.color-show {
  :deep(.checkbox-container) {
    display: flex;
    flex-wrap: wrap;
    gap: 24px 12px
  }
}

.disable-scroll {
  overflow: hidden;
}

:deep(.valueCount) {
  color: $gray-1-color;
}

//  TODO remove once atomic-ui is updated
:deep(.form-error.is-visible) {
  display: none !important;;
}
</style>
